/**
 * Deterministic fixed-step scent left by Rosalie and read by a dog's nose.
 *
 * This contains no scene, physics, clock, or visibility dependencies. Scent is
 * deliberately a second detection language rather than a dimmer version of
 * sight: a dog can replay where Rosalie went after she has disappeared.
 */

/**
 * World-space position used by scent. This duplicates stealth's small shape on
 * purpose: importing it would couple the two detection channels and invite a
 * later convenience change to make dogs reason about light or cones.
 */
export interface Position {
  x: number;
  y: number;
  z: number;
}

/**
 * Axis-aligned volume used by water and slurry to wash scent from the floor.
 * Kept local for the same reason as `Position`: scent must stay independent of
 * the sight system even though both systems use simple world-space data.
 */
export interface Aabb {
  min: Position;
  max: Position;
}

/** One floor-level trace of where Rosalie was, newest when its age is lowest. */
export interface ScentSample {
  x: number;
  z: number;
  age: number;
  strength: number;
  /** Decided at deposit time so rain cannot rewrite where Rosalie was safe. */
  lifetime: number;
}

export type TrackerMode = 'idle' | 'tracking' | 'casting' | 'lost';

export interface TrackerState {
  x: number;
  z: number;
  /** Radians: 0 faces +Z, increasing clockwise toward +X. */
  facing: number;
  mode: TrackerMode;
  /** Time spent unable to smell a trail after release. */
  lostSeconds: number;
}

/**
 * Every authored feel number is kept here rather than hidden in equations.
 * The comments describe the player experience, which is the result a designer
 * can tune; implementation detail belongs next to the relevant calculation.
 */
export const SCENT_TUNING = {
  /** Samples every third of a metre make a continuous route without turning one crossing into a cloud. */
  depositEvery: 0.3,
  /** A sheltered trace's maximum lifetime outlives its crossing but cannot leave a corridor remembering an entire play session. */
  lifetimeSeconds: 90,
  /**
   * How long a trace in the rain lasts, seconds. Beat 7's whole mechanic.
   *
   * **It is 2.5 s and it was written at 6, and the arithmetic is why.** A trail
   * is deposited every `depositEvery` (0.3 m) along her path, so the length of
   * live trail behind her is her speed times this number. At a trot (1.9 m/s)
   * six seconds is **eleven metres** — comfortably inside `noseRadius` (6 m), so
   * a dog genuinely picks her up and follows her across open ground, always
   * losing the far end. That reads as a slow chase.
   *
   * The mechanic is not a slow chase. It is *"they do not follow her; they walk
   * from one place she felt safe to the next"* — and what the player has to be
   * able to see is an **absence**. At 2.5 s the live trail is under five metres:
   * a nose already down on her track finishes the metre it is on and then has
   * nothing, which is what this comment claimed and what six seconds did not
   * deliver.
   *
   * **Unplayed**, and one of the two numbers beat 7 most wants a stopwatch on
   * (the other is `BOARDS_BREAK_SPEED`). Filed in `docs/backlog.md`.
   */
  exposedLifetimeSeconds: 2.5,
  /** Six metres lets a released dog acquire a route without making its choice feel telepathic. */
  noseRadius: 6,
  /** Below five percent a trace is too weak to ask the player to understand the dog following it. */
  faintestUseful: 0.05,
} as const;

/** A volume concealing a third of Rosalie is something she is under, not merely beside. */
export const SHELTER_MIN_CONCEALMENT = 0.35;

const EPSILON = 0.000000001;
// At Rosalie's 4.5 m/s roll, 90 seconds contains 4.5 * 90 / 0.3 = 1,350
// sheltered deposits. Fixing that maximum ceiling prevents the long-session
// leak an unbounded trail would hide until the game has already been played for
// a long time; sizing it for exposed rain would drop the samples this mechanic
// must preserve.
const DEFAULT_CAPACITY = Math.ceil((4.5 * SCENT_TUNING.lifetimeSeconds) / SCENT_TUNING.depositEvery);

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`scent ${label} must be finite`);
}

function positive(value: number, label: string): void {
  finite(value, label);
  if (value <= 0) throw new Error(`scent ${label} must be positive`);
}

function nonNegative(value: number, label: string): void {
  finite(value, label);
  if (value < 0) throw new Error(`scent ${label} must not be negative`);
}

function validatePosition(position: Position, label: string): void {
  finite(position.x, `${label}.x`);
  finite(position.y, `${label}.y`);
  finite(position.z, `${label}.z`);
}

function normaliseAngle(angle: number): number {
  let normalised = (angle + Math.PI) % (Math.PI * 2);
  if (normalised <= 0) normalised += Math.PI * 2;
  return normalised - Math.PI;
}

function bearing(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(toX - fromX, toZ - fromZ);
}

export interface ScentTrailOptions {
  /**
   * Whether a deposit position is sheltered from rain. Absent means everything
   * is sheltered, which is exactly today's behaviour.
   */
  readonly sheltered?: (x: number, z: number) => boolean;
  /** Optional diagnostic/test ceiling; production uses the sheltered maximum. */
  readonly capacity?: number;
}

/**
 * A bounded, ordered trail of floor scent. The ring owns mutable samples; the
 * exposed array is a live read-only diagnostic view and callers must not mutate
 * its entries or array structure.
 */
export class ScentTrail {
  readonly #capacity: number;
  readonly #sheltered: (x: number, z: number) => boolean;
  readonly #ring: Array<ScentSample | undefined>;
  readonly #sampleView: ScentSample[] = [];
  readonly #pendingDeposits: Array<{ x: number; z: number }> = [];
  #head = 0;
  #count = 0;
  #previousSource: Position | null = null;
  #distanceSinceDeposit = 0;

  constructor(options?: ScentTrailOptions) {
    const capacity = options?.capacity ?? DEFAULT_CAPACITY;
    if (!Number.isSafeInteger(capacity) || capacity <= 0) {
      throw new Error('scent trail capacity must be a positive safe integer');
    }
    this.#capacity = capacity;
    this.#sheltered = options?.sheltered ?? (() => true);
    this.#ring = new Array<ScentSample | undefined>(capacity);
  }

  /** A live read-only debug/test view in oldest-to-newest order; callers must not mutate it. */
  get samples(): readonly ScentSample[] {
    return this.#sampleView;
  }

  get count(): number {
    return this.#count;
  }

  /** Advance exactly one fixed simulation step, optionally with Rosalie's dry-floor position. */
  step(dt: number, source: Position | null): void {
    if (!Number.isFinite(dt) || dt < 0) throw new Error('scent step dt must be finite and not negative');
    if (source !== null) validatePosition(source, 'source');

    this.#age(dt);
    if (source === null) {
      // Water must break the route completely. Retaining either distance or
      // crossed-but-not-yet-emitted marks creates a bridge across the hose, so
      // the dog can follow a scent through the counter-play that should erase it.
      this.#previousSource = null;
      this.#distanceSinceDeposit = 0;
      this.#pendingDeposits.length = 0;
      return;
    }

    if (this.#previousSource === null) {
      this.#previousSource = { x: source.x, y: source.y, z: source.z };
      this.#addSample(source.x, source.z);
      return;
    }

    this.#queueCrossedMarks(this.#previousSource, source);
    this.#previousSource = { x: source.x, y: source.y, z: source.z };
    // Like Worker.#recordDistance, one fixed step surfaces one deposit at most.
    // The queued marks remain exact, so an unusually long step does not erase
    // portions of the route the next steps still need to reveal.
    this.#emitOnePendingDeposit();
  }

  /** Remove every floor sample within water or slurry and return how many drained away. */
  wash(volume: Aabb): number {
    validatePosition(volume.min, 'wash.min');
    validatePosition(volume.max, 'wash.max');
    if (volume.min.x > volume.max.x || volume.min.y > volume.max.y || volume.min.z > volume.max.z) {
      throw new Error('scent wash volume minimum must not exceed maximum');
    }

    let removed = 0;
    const kept: ScentSample[] = [];
    for (const sample of this.#orderedSamples()) {
      // Scent exists on the floor plane, so an elevated box cannot mysteriously
      // remove it; checking y=0 makes water placement explainable in tools.
      const inside = volume.min.x <= sample.x && sample.x <= volume.max.x
        && volume.min.y <= 0 && 0 <= volume.max.y
        && volume.min.z <= sample.z && sample.z <= volume.max.z;
      if (inside) removed += 1;
      else kept.push(sample);
    }
    if (removed > 0) this.#replaceWith(kept);
    return removed;
  }

  /**
   * Return the freshest useful trace in range, not the nearest one. Equal-age
   * candidates retain the earlier insertion because this scan is oldest-first
   * and replaces only on a strictly fresher sample; that makes ties replayable.
   */
  strongestNear(position: Position, radius: number): ScentSample | null {
    validatePosition(position, 'nose position');
    nonNegative(radius, 'nose radius');
    const radiusSquared = radius * radius;
    let strongest: ScentSample | null = null;
    for (const sample of this.#orderedSamples()) {
      if (sample.strength < SCENT_TUNING.faintestUseful) continue;
      const dx = sample.x - position.x;
      const dz = sample.z - position.z;
      if (dx * dx + dz * dz > radiusSquared) continue;
      if (strongest === null || sample.age < strongest.age) strongest = sample;
    }
    return strongest;
  }

  /** Consume one exact sample when a tracker reaches it; false means it was already removed. */
  consume(sample: ScentSample): boolean {
    const kept: ScentSample[] = [];
    let consumed = false;
    for (const candidate of this.#orderedSamples()) {
      if (!consumed && candidate === sample) {
        consumed = true;
        continue;
      }
      kept.push(candidate);
    }
    if (consumed) this.#replaceWith(kept);
    return consumed;
  }

  #age(dt: number): void {
    if (this.#count === 0 || dt === 0) return;
    const kept: ScentSample[] = [];
    for (const sample of this.#orderedSamples()) {
      sample.age += dt;
      // Linear decay makes the last useful moment readable after the fact. An
      // exponential tail would make a dog give up at a time the player cannot
      // predict, violating the fairness requirement for scent detection.
      sample.strength = Math.max(0, 1 - sample.age / sample.lifetime);
      if (sample.age + EPSILON < sample.lifetime) kept.push(sample);
    }
    this.#replaceWith(kept);
  }

  #queueCrossedMarks(from: Position, to: Position): void {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const distance = Math.hypot(dx, dz);
    if (distance <= EPSILON) return;

    let travelledToMark = SCENT_TUNING.depositEvery - this.#distanceSinceDeposit;
    while (travelledToMark <= distance + EPSILON) {
      const clampedDistance = Math.min(travelledToMark, distance);
      this.#pendingDeposits.push({
        x: from.x + (dx / distance) * clampedDistance,
        z: from.z + (dz / distance) * clampedDistance,
      });
      travelledToMark += SCENT_TUNING.depositEvery;
    }
    this.#distanceSinceDeposit += distance;
    while (this.#distanceSinceDeposit + EPSILON >= SCENT_TUNING.depositEvery) {
      this.#distanceSinceDeposit -= SCENT_TUNING.depositEvery;
    }
  }

  #emitOnePendingDeposit(): void {
    const next = this.#pendingDeposits.shift();
    if (next !== undefined) this.#addSample(next.x, next.z);
  }

  #addSample(x: number, z: number): void {
    const lifetime = this.#sheltered(x, z)
      ? SCENT_TUNING.lifetimeSeconds
      : SCENT_TUNING.exposedLifetimeSeconds;
    const sample: ScentSample = { x, z, age: 0, strength: 1, lifetime };
    if (this.#count < this.#capacity) {
      const index = (this.#head + this.#count) % this.#capacity;
      this.#ring[index] = sample;
      this.#count += 1;
    } else {
      this.#ring[this.#head] = sample;
      this.#head = (this.#head + 1) % this.#capacity;
    }
    this.#refreshView();
  }

  #orderedSamples(): ScentSample[] {
    const ordered: ScentSample[] = [];
    for (let offset = 0; offset < this.#count; offset += 1) {
      const sample = this.#ring[(this.#head + offset) % this.#capacity];
      if (sample === undefined) throw new Error('scent ring buffer lost a live sample');
      ordered.push(sample);
    }
    return ordered;
  }

  #replaceWith(samples: readonly ScentSample[]): void {
    this.#ring.fill(undefined);
    this.#head = 0;
    this.#count = 0;
    for (const sample of samples) {
      if (this.#count >= this.#capacity) break;
      this.#ring[this.#count] = sample;
      this.#count += 1;
    }
    this.#refreshView();
  }

  #refreshView(): void {
    this.#sampleView.length = 0;
    this.#sampleView.push(...this.#orderedSamples());
  }
}

/** A deterministic nose that follows scent; encounter movement and catching stay elsewhere. */
export class Tracker {
  readonly #speed: number;
  readonly #turnRate: number;
  readonly #noseRadius: number;
  readonly #giveUpSeconds: number;
  readonly #state: TrackerState;
  #lastConsumedAge: number | null = null;

  constructor(definition: {
    readonly speed: number;
    readonly turnRate: number;
    readonly noseRadius?: number;
    readonly giveUpSeconds: number;
    readonly start: Position;
  }) {
    positive(definition.speed, 'tracker speed');
    positive(definition.turnRate, 'tracker turn rate');
    if (definition.noseRadius !== undefined) positive(definition.noseRadius, 'tracker nose radius');
    nonNegative(definition.giveUpSeconds, 'tracker give-up seconds');
    validatePosition(definition.start, 'tracker start');

    this.#speed = definition.speed;
    this.#turnRate = definition.turnRate;
    this.#noseRadius = definition.noseRadius ?? SCENT_TUNING.noseRadius;
    this.#giveUpSeconds = definition.giveUpSeconds;
    this.#state = {
      x: definition.start.x,
      z: definition.start.z,
      facing: 0,
      mode: 'idle',
      lostSeconds: 0,
    };
  }

  /** A read-only view of current state. Callers must not mutate this object. Same identity every call. */
  get state(): Readonly<TrackerState> {
    return this.#state;
  }

  /** Release the dog to reacquire a trail, optionally from its handler's chosen hand-off point. */
  release(at?: Position): void {
    if (at !== undefined) {
      validatePosition(at, 'tracker release position');
      this.#state.x = at.x;
      this.#state.z = at.z;
    }
    this.#state.mode = 'casting';
    this.#state.lostSeconds = 0;
    this.#lastConsumedAge = null;
  }

  /** Hold the dog at the handler's heel; positioning that heel is encounter business, not scent. */
  hold(): void {
    this.#state.mode = 'idle';
    this.#state.lostSeconds = 0;
    this.#lastConsumedAge = null;
  }

  /** Advance exactly one fixed simulation step and return the stable state object. */
  step(dt: number, trail: ScentTrail): Readonly<TrackerState> {
    if (!Number.isFinite(dt) || dt < 0) throw new Error('tracker step dt must be finite and not negative');
    if (this.#state.mode === 'idle' || this.#state.mode === 'lost') return this.#state;

    const strongest = trail.strongestNear({ x: this.#state.x, y: 0, z: this.#state.z }, this.#noseRadius);
    // Reaching a route's newest mark puts its already-walked, closely-spaced
    // marks directly under the dog's nose. Retargeting one of those older marks
    // makes a successful trail replay reverse itself; only a mark at least as
    // fresh as the last one eaten can extend the route rather than undo it.
    const sample = strongest !== null && (this.#lastConsumedAge === null
      || strongest.age <= this.#lastConsumedAge + EPSILON)
      ? strongest
      : null;
    if (sample === null) {
      this.#cast(dt);
      return this.#state;
    }

    this.#state.mode = 'tracking';
    this.#state.lostSeconds = 0;
    const dx = sample.x - this.#state.x;
    const dz = sample.z - this.#state.z;
    const distance = Math.hypot(dx, dz);
    if (distance <= EPSILON) {
      this.#state.x = sample.x;
      this.#state.z = sample.z;
      this.#consume(trail, sample);
      return this.#state;
    }

    const elapsed = Math.min(dt, distance / this.#speed);
    this.#turnToward(bearing(this.#state.x, this.#state.z, sample.x, sample.z), elapsed);
    const travelled = elapsed * this.#speed;
    this.#state.x += (dx / distance) * travelled;
    this.#state.z += (dz / distance) * travelled;
    if (elapsed + EPSILON >= distance / this.#speed) {
      this.#state.x = sample.x;
      this.#state.z = sample.z;
      this.#consume(trail, sample);
    }
    return this.#state;
  }

  #cast(dt: number): void {
    this.#state.mode = 'casting';
    this.#state.lostSeconds += dt;
    // Equality is terminal too: fixed-step clocks should not make an authored
    // give-up time depend on whether their tick happened to land exactly on it.
    if (this.#state.lostSeconds + EPSILON >= this.#giveUpSeconds) {
      this.#state.lostSeconds = this.#giveUpSeconds;
      this.#state.mode = 'lost';
    }
  }

  #consume(trail: ScentTrail, sample: ScentSample): void {
    if (trail.consume(sample)) this.#lastConsumedAge = sample.age;
  }

  #turnToward(target: number, dt: number): void {
    const delta = normaliseAngle(target - this.#state.facing);
    const amount = Math.min(Math.abs(delta), this.#turnRate * dt);
    this.#state.facing = normaliseAngle(this.#state.facing + Math.sign(delta) * amount);
  }
}
